import { useHandler } from "@redotech/react-util/hook";
import ChevronDownIcon from "@redotech/redo-icons/chevron-down.svg";
import ChevronUpIcon from "@redotech/redo-icons/chevron-up.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import {
  openOrderDashboardPath,
  registerRateRefreshNotification,
} from "@redotech/redo-merchant-app-common/open-order-notifications";
import { RedoOutboundLabelsRpcClient } from "@redotech/redo-merchant-fulfillment-rpc/client";
import { showNotification } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  memo,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { RedoOutboundLabelsRpcClientContext } from "../outbound-labels-rpc-client";

const POLL_INTERVAL_MS = 5000;

export const RateRefreshNotificationProvider = memo(
  function RateRefreshNotificationProvider({
    children,
  }: {
    children: ReactNode;
  }) {
    const client = useContext(RedoOutboundLabelsRpcClientContext);
    const showing = useRef(false);

    const show = useHandler((teamId: string) => {
      if (!client || showing.current) {
        return;
      }
      showing.current = true;
      showNotification({
        dataTestId: "rate-refresh-status",
        hideCloseX: true,
        noPadding: true,
        notificationKey: "rates",
        onDismiss: () => {
          showing.current = false;
        },
        children: (dismiss) => (
          <RateRefreshStatusDisplay
            client={client}
            dismiss={dismiss}
            teamId={teamId}
          />
        ),
      });
    });

    useEffect(() => {
      if (!client) {
        return;
      }
      return registerRateRefreshNotification(show);
    }, [client, show]);

    return children;
  },
);

const RateRefreshStatusDisplay = memo(function RateRefreshStatusDisplay({
  client,
  dismiss,
  teamId,
}: {
  client: RedoOutboundLabelsRpcClient;
  dismiss: () => void;
  teamId: string;
}) {
  const [expanded, setExpanded] = useState(true);
  const [started, setStarted] = useState(0);
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(false);
  const [ellipsis, setEllipsis] = useState(".");

  useEffect(() => {
    if (done) {
      return;
    }
    const interval = setInterval(() => {
      setEllipsis((current) => (current.length >= 3 ? "." : current + "."));
    }, 1000);
    return () => clearInterval(interval);
  }, [done]);

  useEffect(() => {
    let cancelled = false;
    let sawRefresh = false;

    const timer = setInterval(() => {
      void poll();
    }, POLL_INTERVAL_MS);

    async function poll() {
      try {
        const result = await client.getRefreshTeamRatesProgress({});
        if (cancelled) {
          return;
        }
        if (result.refresh) {
          sawRefresh = true;
          setStarted(result.refresh.started);
          setTotal(result.refresh.total);
          return;
        }
        if (sawRefresh) {
          setDone(true);
          clearInterval(timer);
        }
      } catch (error) {
        console.error("Failed to poll rate refresh progress:", error);
      }
    }

    void poll();

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [client]);

  const percentage = done
    ? 100
    : total > 0
      ? Math.round((started / total) * 100)
      : 0;

  return (
    <Flex dir="column" gap="none" w="xxs">
      <Flex align="center" justify="space-between" p="xl" py="md">
        <Text fontSize="sm" fontWeight="semibold">
          {done ? "Rates refreshed" : `Refreshing rates${ellipsis}`}
        </Text>
        <Flex align="center" gap="sm">
          <RedoButton
            IconTrailing={expanded ? ChevronUpIcon : ChevronDownIcon}
            onClick={() => setExpanded((current) => !current)}
            size="xs"
          />
          <RedoButton IconTrailing={XIcon} onClick={dismiss} size="xs" />
        </Flex>
      </Flex>
      {expanded && (
        <Flex dir="column" gap="sm" pb="md" px="md" w="full">
          <RedoProgressBar
            labelStyle="tooltip"
            percentage={percentage}
            style="thin"
          />
          <RedoButton
            hierarchy="link"
            onClick={() => {
              const navigate = window.__redoOpenOrderNavigate;
              if (!navigate) {
                return;
              }
              navigate(openOrderDashboardPath(teamId));
            }}
            size="xs"
            text="View on dashboard"
          />
        </Flex>
      )}
    </Flex>
  );
});
